import fetchJson from "@/lib/fetchJson";

export interface AssistantHistoryTurn {
  role: "user" | "assistant";
  content: string;
}

export interface AssistantToolCallTrace {
  name: string;
  args: Record<string, unknown>;
  result?: unknown;
}

export interface AssistantResponse {
  answer: string;
  toolCalls: AssistantToolCallTrace[];
}

export interface AssistantStreamHandlers {
  /** Fired for each streamed token of the final answer. */
  onDelta?: (text: string) => void;
  /** Fired when the assistant invokes a data tool. */
  onTool?: (trace: AssistantToolCallTrace) => void;
  /** Fired when intermediate (tool-call) reasoning text should be discarded. */
  onReset?: () => void;
}

export interface PersistedAssistantMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: AssistantToolCallTrace[];
  createdAt?: string;
}

/**
 * Load the caller's persisted thread for a campaign (cross-device rehydrate).
 */
export async function getCampaignAssistantHistory(
  campaignId: number,
): Promise<PersistedAssistantMessage[]> {
  const result = await fetchJson(`/assistant/campaign/${campaignId}/history`);
  if (!result?.messages || !Array.isArray(result.messages)) return [];
  return result.messages as PersistedAssistantMessage[];
}

function backendUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_PROXY !== "no"
  ) {
    return `/api/proxy/backend${normalized}`;
  }
  return `/backend${normalized}`;
}

/**
 * Streams a campaign assistant answer over Server-Sent Events, invoking the
 * supplied handlers as tokens/tools arrive. Resolves with the authoritative
 * final answer + tool traces once the stream completes.
 */
export async function streamCampaignAssistant(
  campaignId: number,
  question: string,
  history: AssistantHistoryTurn[] = [],
  handlers: AssistantStreamHandlers = {},
): Promise<AssistantResponse> {
  const response = await fetch(backendUrl(`/assistant/campaign/${campaignId}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, history }),
  });

  if (!response.ok || !response.body) {
    let message = "Assistant request failed";
    try {
      const data = await response.json();
      message = data?.result?.error || data?.error || message;
    } catch {
      // non-JSON error body; keep default message
    }
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  let answer = "";
  const toolCalls: AssistantToolCallTrace[] = [];
  let streamError: string | null = null;

  const handleEvent = (eventName: string, dataRaw: string) => {
    let payload: any = {};
    try {
      payload = dataRaw ? JSON.parse(dataRaw) : {};
    } catch {
      return;
    }
    switch (eventName) {
      case "delta":
        if (typeof payload.text === "string") {
          answer += payload.text;
          handlers.onDelta?.(payload.text);
        }
        break;
      case "tool":
        toolCalls.push(payload as AssistantToolCallTrace);
        handlers.onTool?.(payload as AssistantToolCallTrace);
        break;
      case "reset":
        answer = "";
        handlers.onReset?.();
        break;
      case "done":
        if (typeof payload.answer === "string") answer = payload.answer;
        if (Array.isArray(payload.toolCalls)) {
          toolCalls.length = 0;
          toolCalls.push(...payload.toolCalls);
        }
        break;
      case "error":
        streamError = payload.error || "Assistant request failed";
        break;
    }
  };

  const processChunk = (chunk: string) => {
    buffer += chunk;
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).replace(/^ /, ""));
        }
      }
      handleEvent(eventName, dataLines.join("\n"));
      boundary = buffer.indexOf("\n\n");
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    processChunk(decoder.decode(value, { stream: true }));
  }
  processChunk(decoder.decode());

  if (streamError) {
    throw new Error(streamError);
  }
  if (!answer) {
    throw new Error("Assistant returned an empty response");
  }

  return { answer, toolCalls };
}
